import React from "react";
import { FaCheckCircle } from "react-icons/fa";
import { getTxExplorerUrl } from "../../helper/x402/chains";

/**
 * Render a receipt timestamp as e.g. "Apr 22, 2026 at 3:42 PM".
 *
 * Uses `Intl.DateTimeFormat` with locale `en-US` — the webapp isn't
 * currently i18n'd on the payment page (English-only strings above),
 * so pinning the locale keeps the format stable regardless of the
 * browser's configured language. Falls back to an ISO string on the
 * (unlikely) formatter error so the receipt never renders blank.
 */
function formatTimestamp(ms) {
  if (!ms || !Number.isFinite(Number(ms))) return null;
  try {
    const d = new Date(Number(ms));
    const date = d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${date} at ${time}`;
  } catch {
    return new Date(Number(ms)).toISOString();
  }
}

function shortenAddress(addr) {
  if (!addr || addr.length < 10) return addr || "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Post-success receipt panel.
 *
 * The 402 Lambda's 200 body (shape in
 * `backend/wallet-management/src/functions/paymentLink/handler.ts`
 * lines 301-320) carries:
 *   - paid: true
 *   - linkId
 *   - amount_atomic / amount_display / amount_usd / token_symbol
 *   - description
 *   - uses_count  (the new count after this payment)
 *   - receipt: { tx_hash, paid_by, timestamp }
 *
 * We surface tx_hash + an explorer link (when we have one for the
 * chain) + the amount + the recipient. No "share this receipt"
 * button in v1 — the URL is the share handle; whatever tool the
 * user was using to share the payment link is equally fine for
 * sharing the post-payment state (`GET` on the same UUID after
 * payment returns the updated `uses_count`, which the webapp could
 * turn into a "paid by X users" receipt page later).
 */
export function ReceiptCard({ receipt, link, method }) {
  // The 402 Lambda (x402 flow) returns `receipt.receipt.tx_hash`
  // + `.paid_by` in snake_case. The direct-transfer Lambda returns
  // `receipt.receipt.txHash` + `.from` in camelCase. Normalize
  // both shapes here so ReceiptCard is paid-through-either-path
  // without the caller branching.
  const txHash =
    receipt?.receipt?.tx_hash ||
    receipt?.receipt?.txHash ||
    "pending";
  const paidBy =
    receipt?.receipt?.paid_by ||
    receipt?.receipt?.from ||
    null;
  // Method is explicit on direct payments (passed by DirectPanel)
  // and inferred as x402 otherwise. The direct-transfer Lambda also
  // puts `method: 'direct'` in the receipt for symmetry.
  const resolvedMethod =
    method || receipt?.receipt?.method || "x402";
  const explorerUrl = getTxExplorerUrl(link?.chainId, txHash);

  return (
    <div className="pay-receipt">
      <h3 className="pay-receipt__title">
        <FaCheckCircle size={20} color="#1f6a1f" /> Payment complete
      </h3>

      <div className="pay-receipt__row">
        <span className="pay-receipt__label">Amount</span>
        <span className="pay-receipt__value">
          {receipt?.amount_display ||
            receipt?.receipt?.amountDisplay ||
            link?.amountDisplay}
        </span>
      </div>

      {/* Method row — payers can see at a glance whether they
       * paid via gasless x402 or a direct on-chain transfer.
       * Useful for merchants reconciling between receipt types
       * too, since the two Lambdas write different receipt rows. */}
      <div className="pay-receipt__row">
        <span className="pay-receipt__label">Method</span>
        <span className="pay-receipt__value">
          {resolvedMethod === "direct"
            ? "Direct transfer"
            : "Gasless (x402)"}
        </span>
      </div>

      {link?.description && (
        <div className="pay-receipt__row">
          <span className="pay-receipt__label">For</span>
          <span className="pay-receipt__value">{link.description}</span>
        </div>
      )}

      {paidBy && (
        <div className="pay-receipt__row">
          <span className="pay-receipt__label">From</span>
          <span className="pay-receipt__value">{shortenAddress(paidBy)}</span>
        </div>
      )}

      {/* Recipient row — the wallet that received the payment. Shows
       * @username when the link carries one (creator.username), plus
       * the canonical short address in a monospace span so auditors
       * can match it against explorer output. Both come from the
       * /info response; no inference. */}
      {link?.payTo && (
        <div className="pay-receipt__row">
          <span className="pay-receipt__label">To</span>
          <span className="pay-receipt__value">
            {link.creator?.username ? `@${link.creator.username} ` : ""}
            <span className="pay-receipt__address-mono">
              ({shortenAddress(link.payTo)})
            </span>
          </span>
        </div>
      )}

      {/* Timestamp — the Lambda returns `receipt.timestamp` as
       * ms-since-epoch. formatTimestamp pins to en-US so the
       * format stays stable regardless of browser locale. */}
      {receipt?.receipt?.timestamp && (
        <div className="pay-receipt__row">
          <span className="pay-receipt__label">Time</span>
          <span className="pay-receipt__value">
            {formatTimestamp(receipt.receipt.timestamp)}
          </span>
        </div>
      )}

      <div className="pay-receipt__row">
        <span className="pay-receipt__label">Transaction</span>
        <span className="pay-receipt__value">
          {/* tx_hash may be "pending" for the first couple of seconds
           * while the facilitator finalizes; render it verbatim
           * rather than disappearing the row. */}
          {txHash.length > 10
            ? `${txHash.slice(0, 10)}…${txHash.slice(-8)}`
            : txHash}
        </span>
      </div>

      {explorerUrl && (
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pay-receipt__link"
        >
          View on explorer →
        </a>
      )}
    </div>
  );
}
